/**
 * GAMEPLAY_CONTENT — binds each gameplay-kind WindowId to its content component.
 *
 * The gameplay half of the engine's "what each window renders" layer
 * (registry.ts = metadata, WindowManagerContext = runtime state, this =
 * id→component for `kind: 'gameplay'`). Phase 2 folds the player-facing windows
 * off the legacy UIContext-flag + GameUIOverlay-inline-mount path; each window
 * exports a propless CONTENT component (body only) that WindowHost wraps in the
 * shared ToolWindow chrome.
 *
 * dependency-cruiser: these are GAMEPLAY components — they live under
 * `ui/Windows/` and may read gameplay state (room/storage/singletons), so they
 * MUST go here, NEVER in content.diagnostic.tsx. The `no-gameplay-ui-to-diagnostics`
 * boundary keeps the two maps apart; mixing them would break the gate.
 *
 * Player-dependent Contents (party/jobs/talents/…) read useGame() directly —
 * GameProvider wraps the root render (the 2026-06-05 keystone), so player
 * state is reachable from every window the engine mounts.
 *
 * Merged into the exhaustive `Record<WindowId, _>` by content.ts (the tsc
 * completeness guard) + pinned by the runtime completeness test.
 */

import type { WindowContentMap } from './types';
import { SettingsContent } from '../Windows/SettingsWindow';
import { KeyBindingsWindow } from '../Windows/KeyBindings/KeyBindingsWindow';
import { TwitchPiPContent } from '../Windows/TwitchPiPWindow';
import { BankContent } from '../Windows/Inventory/BankWindow';
import { CraftingContent } from '../Windows/Inventory/CraftingWindow';
import { LobbyToolsContent } from '../Windows/LobbyToolsPanel';
import { TradeContent } from '../Windows/Inventory/TradeWindow';
import { PartyContent } from '../Windows/PartyPanel';
import { JobsContent } from '../Windows/JobsWindow';
import { TalentsContent } from '../Windows/TalentsWindow';
import { CharacterContent } from '../Windows/EquipmentWindow';
import { InventoryContent } from '../Windows/InventoryUI';
import { AbilityBrowserContent } from '../Windows/AbilityBrowser';
import { QuestContent } from '../Windows/QuestUI';
import { SimulationContent } from '../Windows/SimulationControlPanel';
import { LogViewerContent } from '../../../components/LogViewer';
import { NPCWindowViewerContent } from '../Windows/NPCWindowViewer';
import { DevToolsContent } from '../Windows/DevToolsPanel';
import { QAPanelContent } from '../Windows/QA/QAPanel';
import { ContractDashboardContent } from '../Windows/ContractDashboard';
import { CameraControlContent } from '../Windows/CameraControlWindow';
import { SimulationToolsContent } from '../Windows/SimulationToolsWindow';
import { MapToolsContent } from '../Windows/MapToolsWindow';
import { MapPickerContent } from '../Overlays/MapPickerOverlay';
import { GmEventAnnouncementContent } from '../Windows/GmEvent/GmEventAnnouncement';
import { GmEventScoreboardContent } from '../Windows/GmEvent/GmEventScoreboard';
import { RaceLobbyWindowContent } from '../Windows/GmEvent/RaceLobbyWindow';
import { GmEventManagerContent } from '../Windows/GmEvent/GmEventManager';
import { GmLobbyToolsContent } from '../Windows/Gm/GmLobbyToolsWindow';
import { GmPlayerToolsContent } from '../Windows/Gm/GmPlayerToolsWindow';
import { GmHazardLabContent } from '../Windows/Gm/GmHazardLabWindow';
import { GmSoundDebugContent } from '../Windows/Gm/GmSoundDebugWindow';
import { SpeedrunRecordsContent } from '../Windows/SpeedrunRecordsWindow';
import { WorldMapContent } from '../Windows/WorldMap/WorldMapWindow';
import { LeaderboardContent } from '../Windows/Social/leaderboard/LeaderboardWindow';
import { InspectContent } from '../Windows/Social/inspect/InspectWindow';
import { ChestLootContent } from '../Windows/Loot/ChestLootWindow';

/** Gameplay-kind id→component map. Keyed by WindowId (partial — only the
 *  gameplay ids); content.ts merges the per-kind maps into the exhaustive
 *  `Record<WindowId, React.FC>`. */
export const GAMEPLAY_CONTENT: WindowContentMap = {
  settings: SettingsContent,
  keyBindings: KeyBindingsWindow,
  twitchPiP: TwitchPiPContent,
  bank: BankContent,
  crafting: CraftingContent,
  lobbyTools: LobbyToolsContent,
  trade: TradeContent,
  party: PartyContent,
  jobs: JobsContent,
  talents: TalentsContent,
  equipment: CharacterContent,
  inventory: InventoryContent,
  spells: AbilityBrowserContent,
  quests: QuestContent,
  speedrunRecords: SpeedrunRecordsContent,
  worldMap: WorldMapContent,
  leaderboard: LeaderboardContent,
  inspect: InspectContent,
  chestLoot: ChestLootContent,
  simulationPanel: SimulationContent,
  logViewer: LogViewerContent,
  npcWindowViewer: NPCWindowViewerContent,
  devTools: DevToolsContent,
  qaPanel: QAPanelContent,
  contractDashboard: ContractDashboardContent,
  cameraControl: CameraControlContent,
  simulationTools: SimulationToolsContent,
  mapTools: MapToolsContent,
  mapPicker: MapPickerContent,
  gmEventAnnouncement: GmEventAnnouncementContent,
  gmEventScoreboard: GmEventScoreboardContent,
  raceLobby: RaceLobbyWindowContent,
  gmEventManager: GmEventManagerContent,
  gmLobbyTools: GmLobbyToolsContent,
  gmPlayerTools: GmPlayerToolsContent,
  gmHazardLab: GmHazardLabContent,
  gmSoundDebug: GmSoundDebugContent,
};
